/**
 * ConnectorConfigDialog — create + edit form for a connector.
 *
 * Opens as a centered modal (matches PublishDialog). Mode is
 * inferred: a `connectorId` selects edit; a `createType` selects
 * create. The viewport-relative `left-1/2 -translate-x-1/2` dodges
 * bidi origin on purpose — see PublishDialog's comment.
 */

import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Spinner } from '@/components/LoadingState';
import type {
  Connector,
  ConnectorAuth,
  ConnectorConfig,
  ConnectorStatus,
  ConnectorType,
  ConnectorTestResult,
  EntityId,
  GiSummary,
} from '@/api/types';
import {
  useCreateConnector,
  useDeleteConnector,
  useTestConnector,
  useUpdateConnector,
} from '@/hooks/useConnectors';

const AUTHS: ConnectorAuth[] = ['none', 'api_key', 'bearer', 'basic', 'oauth2'];
const STATUSES: ConnectorStatus[] = ['active', 'disabled', 'error'];

/** Which connector types support HTTP-style headers in the form. */
const HEADER_TYPES: ReadonlySet<ConnectorType> = new Set([
  'rest_api',
  'webhook',
  'file_storage',
]);

export type DialogMode =
  | { kind: 'create'; type: ConnectorType; linkedGi?: EntityId }
  | { kind: 'edit'; connector: Connector };

export function ConnectorConfigDialog({
  mode,
  open,
  onOpenChange,
  gis,
}: {
  mode: DialogMode | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  gis: GiSummary[];
}) {
  const { t } = useTranslation(['connectors', 'common']);

  const create = useCreateConnector();
  const update = useUpdateConnector();
  const remove = useDeleteConnector();
  const test = useTestConnector();

  // Derived once per open so edits don't fight in-flight state.
  const initial = useMemo(() => buildInitial(mode), [mode]);

  const [name, setName] = useState(initial.name);
  const [endpoint, setEndpoint] = useState(initial.endpoint);
  const [auth, setAuth] = useState<ConnectorAuth>(initial.auth);
  const [headersText, setHeadersText] = useState(initial.headersText);
  const [notes, setNotes] = useState(initial.notes);
  const [status, setStatus] = useState<ConnectorStatus>(initial.status);
  const [linked, setLinked] = useState<Set<EntityId>>(new Set(initial.linked));
  const [testResult, setTestResult] = useState<ConnectorTestResult | null>(null);

  // Reset local state whenever the dialog's mode identity changes.
  useEffect(() => {
    setName(initial.name);
    setEndpoint(initial.endpoint);
    setAuth(initial.auth);
    setHeadersText(initial.headersText);
    setNotes(initial.notes);
    setStatus(initial.status);
    setLinked(new Set(initial.linked));
    setTestResult(null);
  }, [initial]);

  if (!mode) return null;

  const isEdit = mode.kind === 'edit';
  const currentType: ConnectorType = isEdit ? mode.connector.type : mode.type;
  const showHeaders = HEADER_TYPES.has(currentType);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!mode) return;
    if (create.isPending || update.isPending) return;
    const config: ConnectorConfig = {
      endpoint: endpoint.trim(),
      auth,
      headers: parseHeaders(headersText),
      notes: notes.trim(),
    };
    if (mode.kind === 'edit') {
      await update.mutateAsync({
        id: mode.connector.id,
        name: name.trim() || t('form.unnamed'),
        config,
        status,
        linked_gi_ids: Array.from(linked),
      });
    } else {
      await create.mutateAsync({
        name: name.trim() || t('form.unnamed'),
        type: mode.type,
        config,
        linked_gi_ids: Array.from(linked),
      });
    }
    onOpenChange(false);
  }

  async function onTest() {
    if (!mode || mode.kind !== 'edit') {
      // Create-mode: optimistic local test — synthesizes an "ok"
      // result without persisting, so the user can sanity-check the
      // form before saving.
      setTestResult({
        ok: true,
        message: t('form.test_local_ok'),
        latency_ms: 0,
      });
      return;
    }
    const r = await test.mutateAsync(mode.connector.id);
    setTestResult(r);
  }

  async function onDelete() {
    if (!mode || mode.kind !== 'edit') return;
    // Single confirm via native prompt keeps the delete path short —
    // a full undo would need toast infrastructure we don't have yet.
    const ok = window.confirm(t('form.delete_confirm', { name: mode.connector.name }));
    if (!ok) return;
    await remove.mutateAsync(mode.connector.id);
    onOpenChange(false);
  }

  function toggleLinked(id: EntityId) {
    setLinked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const pending = create.isPending || update.isPending || remove.isPending;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[min(92vw,620px)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-surface p-6 shadow-float focus:outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">
            {isEdit ? t('form.edit_title') : t(`form.create_title.${currentType}`)}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            {t(`types.${currentType}.blurb`)}
          </Dialog.Description>

          <form onSubmit={onSubmit} className="mt-5 flex flex-col gap-4">
            <Field label={t('form.name_label')} hint={t('form.name_hint')}>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={64}
                className={inputCls}
                required
              />
            </Field>

            <Field label={t(`form.endpoint_label.${currentType}`)} hint={t(`form.endpoint_hint.${currentType}`)}>
              <input
                type="text"
                value={endpoint}
                onChange={(e) => setEndpoint(e.target.value)}
                className={inputCls + ' font-mono text-xs'}
                placeholder={t(`form.endpoint_placeholder.${currentType}`)}
                required
              />
            </Field>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label={t('form.auth_label')} hint={t('form.auth_hint')}>
                <select
                  value={auth}
                  onChange={(e) => setAuth(e.target.value as ConnectorAuth)}
                  className={inputCls}
                >
                  {AUTHS.map((a) => (
                    <option key={a} value={a}>
                      {t(`form.auth.${a}`)}
                    </option>
                  ))}
                </select>
              </Field>

              {isEdit ? (
                <Field label={t('form.status_label')} hint={t('form.status_hint')}>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as ConnectorStatus)}
                    className={inputCls}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {t(`status.${s}`)}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : null}
            </div>

            {showHeaders ? (
              <Field label={t('form.headers_label')} hint={t('form.headers_hint')}>
                <textarea
                  rows={3}
                  value={headersText}
                  onChange={(e) => setHeadersText(e.target.value)}
                  className={inputCls + ' font-mono text-xs'}
                  placeholder={'x-api-key=sk_live_…\nx-env=production'}
                />
              </Field>
            ) : null}

            <Field label={t('form.notes_label')} hint={t('form.notes_hint')}>
              <textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={inputCls}
              />
            </Field>

            {gis.length > 0 ? (
              <Field label={t('form.linked_label')} hint={t('form.linked_hint')}>
                <ul className="flex max-h-36 flex-col gap-1 overflow-y-auto rounded-md border border-border bg-canvas p-2">
                  {gis.map((g) => (
                    <li key={g.root_id}>
                      <label className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-surface-raised">
                        <input
                          type="checkbox"
                          checked={linked.has(g.root_id)}
                          onChange={() => toggleLinked(g.root_id)}
                          className="h-4 w-4 accent-spark"
                        />
                        <span className="min-w-0 flex-1 truncate text-ink">{g.name}</span>
                        <span className="font-mono text-[11px] text-ink-subtle">v{g.version}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </Field>
            ) : null}

            {/* Test row */}
            <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-canvas p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink">{t('form.test_title')}</p>
                {testResult ? (
                  <p
                    className={
                      'mt-0.5 text-xs ' +
                      (testResult.ok ? 'text-success' : 'text-warn')
                    }
                  >
                    {testResult.message}
                  </p>
                ) : (
                  <p className="mt-0.5 text-xs text-ink-muted">{t('form.test_hint')}</p>
                )}
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => void onTest()}
                disabled={test.isPending}
              >
                {test.isPending ? <Spinner /> : t('form.test_button')}
              </Button>
            </div>

            <div className="mt-2 flex items-center justify-between gap-2 border-t border-border pt-4">
              {isEdit ? (
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => void onDelete()}
                  disabled={pending}
                >
                  {t('form.delete')}
                </Button>
              ) : (
                <span aria-hidden="true" />
              )}
              <div className="flex items-center gap-2">
                <Dialog.Close asChild>
                  <Button type="button" variant="ghost">
                    {t('actions.cancel', { ns: 'common' })}
                  </Button>
                </Dialog.Close>
                <Button type="submit" variant="primary" disabled={pending}>
                  {pending
                    ? t('form.saving')
                    : isEdit
                      ? t('form.save')
                      : t('form.create')}
                </Button>
              </div>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────

const inputCls =
  'rounded-md border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:border-spark focus-visible:outline-none';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-medium text-ink">{label}</span>
      {children}
      {hint ? <span className="text-xs text-ink-muted">{hint}</span> : null}
    </label>
  );
}

function parseHeaders(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
}

interface Initial {
  name: string;
  endpoint: string;
  auth: ConnectorAuth;
  headersText: string;
  notes: string;
  status: ConnectorStatus;
  linked: EntityId[];
}

function buildInitial(mode: DialogMode | null): Initial {
  if (!mode) {
    return {
      name: '',
      endpoint: '',
      auth: 'none',
      headersText: '',
      notes: '',
      status: 'active',
      linked: [],
    };
  }
  if (mode.kind === 'edit') {
    return {
      name: mode.connector.name,
      endpoint: mode.connector.config.endpoint,
      auth: mode.connector.config.auth,
      headersText: mode.connector.config.headers.join('\n'),
      notes: mode.connector.config.notes,
      status: mode.connector.status,
      linked: mode.connector.linked_gi_ids,
    };
  }
  return {
    name: '',
    endpoint: '',
    auth: 'none',
    headersText: '',
    notes: '',
    status: 'active',
    linked: mode.linkedGi ? [mode.linkedGi] : [],
  };
}
